import React from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { TabsContent } from '../ui/tabs';
import { toast } from 'sonner';
import { offersAPI } from '../../lib/api';
import { getScoreColor } from '../../lib/scoreUtils';

export default function CandidateInterviewsTab({ candidate, candidateId, parsed, match, scoreBreakdown, matchedSkillsSet, depthAnalysis, analyzingDepth, runDepthAnalysis, offerProb, pipelineHistory, offer, showOfferForm, setShowOfferForm, offerForm, setOfferForm, onboardingPlan, setOnboardingPlan, loadCandidate }) {
  return (
        <TabsContent value="interviews">
          {/* Sent interview invitation links */}
          {(() => {
            const ivInvites = (candidate.sent_invitations || []).filter(n => n.type === 'interview_assigned');
            if (ivInvites.length > 0) return (
              <div style={{ marginBottom: '1.25rem' }}>
                <h4 style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#64748B', marginBottom: '0.625rem' }}>Sent Interview Links</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {ivInvites.map(inv => (
                    <div key={inv._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', borderRadius: 10, background: '#F1EAE1', border: '1px solid #ECE7DE' }}>
                      <div>
                        <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#0076A3' }}>{inv.data?.template_name || 'Interview'}</div>
                        <div style={{ fontSize: '0.6875rem', color: '#64748B' }}>
                          Sent: {inv.created_at ? new Date(inv.created_at).toLocaleDateString() : ''} &middot; {inv.data?.job_title || ''}
                          {inv.email_sent && <span style={{ color: '#059669', marginLeft: '0.375rem' }}>Email sent</span>}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.375rem' }}>
                        {inv.data?.access_code && (
                          <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/join-interview/${inv.data.access_code}`); toast.success('Interview link copied!'); }}
                            style={{ padding: '0.3125rem 0.625rem', borderRadius: 6, border: '1px solid #ECE7DE', background: '#fff', cursor: 'pointer', fontSize: '0.6875rem', fontWeight: 600, color: '#0076A3' }}>
                            Copy Link
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ); return null;
          })()}

          {(candidate.interview_sessions || []).length === 0 && (candidate.sent_invitations || []).filter(n => n.type === 'interview_assigned').length === 0 ? (
            <div className="empty-state" style={{ paddingTop: '2rem' }}>
              <p>No interviews sent or conducted with this candidate yet.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {(candidate.interview_sessions || []).map(sess => {
                const summary = sess.summary || {};
                const scored = sess.status === 'scored';
                return (
                  <Card key={sess._id} style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                      <div>
                        <h4 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 600, color: '#0076A3' }}>{sess.template_name || 'Interview'}</h4>
                        <div style={{ fontSize: '0.75rem', color: '#64748B' }}>
                          {sess.candidate_email} &middot; {sess.started_at ? new Date(sess.started_at).toLocaleDateString() : ''}
                          &middot; Status: <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{sess.status}</span>
                        </div>
                      </div>
                      {summary.overall_score != null && (
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontFamily: 'Cabinet Grotesk', fontSize: '2rem', fontWeight: 700, color: summary.overall_score >= 70 ? '#059669' : '#D97706' }}>
                            {summary.overall_score}%
                          </div>
                          <div style={{ fontSize: '0.6875rem', color: '#64748B' }}>{summary.overall_rating}</div>
                        </div>
                      )}
                    </div>

                    {/* Score dimensions */}
                    {scored && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem', marginBottom: '1rem' }}>
                        {[
                          { label: 'Communication', val: summary.communication_score },
                          { label: 'Technical Depth', val: summary.technical_depth_score },
                          { label: 'Cultural Fit', val: summary.cultural_fit_score },
                        ].filter(d => d.val != null).map(d => (
                          <div key={`dim-${sess._id}-${d.label}`} style={{ padding: '0.75rem', borderRadius: 10, background: '#FAF7F2', border: '1px solid #F0EBE3', textAlign: 'center' }}>
                            <div style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.25rem', fontWeight: 700, color: d.val >= 7 ? '#059669' : d.val >= 5 ? '#D97706' : '#DC2626' }}>{d.val}/10</div>
                            <div style={{ fontSize: '0.625rem', color: '#64748B' }}>{d.label}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Per-question scores */}
                    {(sess.scores || []).length > 0 && (
                      <div style={{ marginBottom: '1rem' }}>
                        <h5 style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', marginBottom: '0.625rem' }}>Questions & Responses</h5>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          {(sess.questions || []).map((q, qi) => {
                            const s = (sess.scores || [])[qi] || {};
                            const transcript = (sess.transcripts || [])[qi] || '(No response)';
                            const scorePct = (s.max_score || 10) > 0 ? (s.score || 0) / (s.max_score || 10) : 0;

                            return (
                              <div key={`iv-qa-${sess._id}-${qi}`} style={{ borderRadius: 10, border: '1px solid #ECE7DE', overflow: 'hidden' }}>
                                {/* Question */}
                                <div style={{ padding: '0.75rem 1rem', background: '#FAF7F2', borderBottom: '1px solid #ECE7DE', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem' }}>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.375rem' }}>
                                      <span style={{ fontFamily: 'JetBrains Mono', fontSize: '0.625rem', color: '#94A3B8' }}>Q{qi + 1}</span>
                                      {q.skill_tested && <span style={{ padding: '0.0625rem 0.375rem', borderRadius: 999, fontSize: '0.5625rem', fontWeight: 600, background: 'rgba(30,104,255,0.08)', color: '#0076A3' }}>{q.skill_tested}</span>}
                                      {q.time_limit_seconds && <span style={{ fontSize: '0.5625rem', color: '#94A3B8' }}>{Math.ceil(q.time_limit_seconds / 60)} min</span>}
                                    </div>
                                    <p style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#0076A3', lineHeight: 1.5 }}>{q.question}</p>
                                  </div>
                                  <div style={{ textAlign: 'center', flexShrink: 0 }}>
                                    <div style={{ fontFamily: 'JetBrains Mono', fontSize: '1rem', fontWeight: 700, color: scorePct >= 0.7 ? '#059669' : scorePct >= 0.5 ? '#D97706' : '#DC2626' }}>
                                      {s.score}/{s.max_score || 10}
                                    </div>
                                  </div>
                                </div>

                                {/* Candidate's response */}
                                <div style={{ padding: '0.75rem 1rem' }}>
                                  <div style={{ padding: '0.625rem 0.875rem', borderRadius: 8, background: '#FAF7F2', border: '1px solid #F0EBE3', fontSize: '0.8125rem', color: '#4A5568', whiteSpace: 'pre-wrap', lineHeight: 1.6, maxHeight: 200, overflow: 'auto' }}>
                                    {transcript}
                                  </div>

                                  {s.feedback && (
                                    <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#64748B', fontStyle: 'italic' }}>
                                      <strong>Feedback:</strong> {s.feedback}
                                    </div>
                                  )}
                                  {(s.strengths || []).length > 0 && (
                                    <div style={{ marginTop: '0.375rem', fontSize: '0.6875rem', color: '#059669' }}>
                                      <strong>Strengths:</strong> {s.strengths.join(', ')}
                                    </div>
                                  )}
                                  {(s.weaknesses || []).length > 0 && (
                                    <div style={{ marginTop: '0.25rem', fontSize: '0.6875rem', color: '#D97706' }}>
                                      <strong>Areas to improve:</strong> {s.weaknesses.join(', ')}
                                    </div>
                                  )}
                                  {s.key_observations && (
                                    <div style={{ marginTop: '0.25rem', fontSize: '0.6875rem', color: '#64748B' }}>
                                      <strong>Observations:</strong> {s.key_observations}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Recommendation */}
                    {summary.recommendation && (
                      <div style={{ padding: '0.625rem 0.875rem', borderRadius: 8, background: 'rgba(30,104,255,0.06)', border: '1px solid rgba(30,104,255,0.12)', fontSize: '0.8125rem' }}>
                        <strong style={{ color: '#0076A3' }}>Recommendation:</strong> <span style={{ color: '#4A5568' }}>{summary.recommendation}</span>
                      </div>
                    )}

                    {/* Summary */}
                    {summary.summary && (
                      <div style={{ marginTop: '0.75rem', fontSize: '0.8125rem', color: '#4A5568', lineHeight: 1.7 }}>
                        <strong>AI Summary:</strong> {summary.summary}
                      </div>
                    )}

                    {/* Anti-cheat */}
                    {(sess.anti_cheat_events || []).length > 0 && (
                      <div style={{ marginTop: '0.75rem', fontSize: '0.6875rem', color: '#D97706' }}>
                        {sess.anti_cheat_events.length} integrity event{sess.anti_cheat_events.length !== 1 ? 's' : ''} logged
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
  );
}